<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <form-card
            :title="$t('pages.videoCategoryList.detailModal.order.title')"
            :description="$t('pages.videoCategoryList.detailModal.order.description')"
          >
            <div class="flex flex-col gap-y-5">
              <TextInput
                v-model="rawCategory.orderNumber"
                type="number"
                name="orderNumber"
                :error="errors?.orderNumber"
                :label="$t('video.orderNumber')"
              />
              <multi-select
                v-model="rawCategory.target"
                :error="errors?.target"
                :searchable="true"
                :label="$t('videoCategory.target.DEFAULT')"
                :placeholder="$t('videoCategory.target.DEFAULT')"
                :options="fetchTargets"
              />
            </div>
          </form-card>
          <form-card
            :title="$t('pages.videoCategoryList.detailModal.general.title')"
            :description="$t('pages.videoCategoryList.detailModal.general.description')"
          >
            <translation-accordion v-slot="{ translation }" v-model="rawCategory">
              <div class="flex flex-col gap-y-5">
                <TextInput
                  v-model="(translation as VideoCategoryTranslation).title"
                  type="text"
                  name="title"
                  :error="errors?.title"
                  :label="$t('video.title')"
                />
                <TextInput
                  v-model="(translation as VideoCategoryTranslation).description"
                  type="text"
                  name="description"
                  :error="errors?.description"
                  :label="$t('video.description')"
                />
              </div>
            </translation-accordion>
          </form-card>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  array, number, object, string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@components/utils/FormCard.vue';
import TextInput from '@components/utils/inputs/Input.vue';
import TranslationAccordion from '@utils/language/TranslationAccordion.vue';
import { syncRef } from '@/hooks/state';
import type { VideoCategory, VideoCategoryTranslation } from '@/entities/Entities';
import { enumOptionsProvider } from '@/components/utils/table/hooks/provider';
import MultiSelect from '@/components/utils/Multiselect.vue';

interface Props {
  open: boolean;
  category?: VideoCategory;
}

const emit = defineEmits(['update:open', 'update:category', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalCategory = useVModel(props, 'category', emit);

const isNew = computed(() => !props.category);
const tKey = computed(() => (isNew.value
  ? 'pages.videoCategoryList.detailModal.new'
  : 'pages.videoCategoryList.detailModal.edit'));

const { t } = useI18n();

const notify = useNotify();

const newTranslation = {
  title: undefined,
  description: undefined,
  language: 'de',
};

interface RawTranslation {
  '@id'?: string;
  id?: string;
  title?: string;
  description?: string;
  language: string;
}

const getMainTranslation = (translations: RawTranslation[]) => translations?.find((translation) => translation?.language === 'de');

const getRawTranslations = (translations: RawTranslation[]): RawTranslation[] => translations.map((translation) => ({
  id: translation['@id'],
  description: translation.description,
  title: translation.title,
  language: translation.language,
}));

const initialData = (category?: VideoCategory): RawTranslation[] => (category
  ? getRawTranslations(
    getMainTranslation(category.translations)
      ? category.translations
      : [{ ...newTranslation } as RawTranslation].concat(category.translations),
  )
  : []);

const rawCategory = syncRef(internalCategory, (category) => ({
  translations: initialData(category),
  orderNumber: category?.orderNumber,
  target: category?.target ?? 'standard',
}));

const {
  data: category,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    translations: array(
      object({
        title: string().nullable(),
        description: string().nullable(),
      }),
    ),
    orderNumber: number().nullable().required(t('validation.notEmpty')),
    target: string().nullable().required(t('validation.notEmpty')),
  }),
  rawCategory,
);

function resetData() {
  rawCategory.value = { translations: initialData() } as unknown as typeof rawCategory['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalCategory.value = undefined;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.category;
    const url = edit ? props.category['@id'] : '/video_categories';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        translations: category.value.translations.filter((trans) => trans.title || trans.description),
        orderNumber: category.value.orderNumber,
        target: category.value.target,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});

const fetchTargets = enumOptionsProvider('/enums/VideoCategoryTarget', (value) => ({
  value,
  label: t(`videoCategory.target.${value}`),
}));
</script>

<style lang="scss" scoped></style>
